import { getCanonicalDomain } from "@guardian/shared-utils";
import { extractBrandSlug } from "@/lib/branding";

// WHY: SPA must not trust the wire — re-validate brandSlug before interpolating into
// a redirect URL, mirroring BrandingProvider's hasRedirectFields guard. The DB CHECK
// constraint prevents arbitrary values today, but a future DB-bypass path that wrote
// brandSlug="evil.com?" would interpolate to https://evil.com?.gcheckin.com/... (open redirect).
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

/**
 * Reconciles the SPA hostname with a public resource's brandSlug in both directions.
 * Shared by FormDataBrandRedirect (form route) and useListingBrandRedirect (listing route).
 *
 * FORWARD (canonical/legacy → branded): on the canonical/legacy host, when the resource
 * qualifies for brand application (non-null brandSlug = active Business-tier brand) AND
 * the owner has auto-redirect enabled, redirects to the branded subdomain preserving
 * path + search.
 *
 * REVERSE (branded → canonical): on a branded subdomain whose slug is absent from the
 * wire (per-listing tier gate suppressed it) or does NOT match the current subdomain
 * (stale cross-Owner link), redirects to the canonical domain.
 *
 * @param brandAutoRedirect boolean | null — NOT coerced to a default; a null wire value
 *   is always paired with a null brandSlug, so the `!brandSlug` guard returns first and
 *   the `=== false` suppression only fires on an explicit owner opt-out.
 * @param ready load-settled gate. Until true the function no-ops, preventing the
 *   reverse↔forward redirect cycle that would arise from acting on a pre-fetch null slug.
 */
export function reconcileBrandHost(args: {
  brandSlug: string | null;
  brandAutoRedirect: boolean | null;
  ready: boolean;
}): void {
  const { brandSlug, brandAutoRedirect, ready } = args;
  if (!ready) return;

  const canonicalDomain = getCanonicalDomain();
  const currentSlug = extractBrandSlug(window.location.hostname);

  if (currentSlug) {
    if (!brandSlug || brandSlug !== currentSlug) {
      const canonical = `https://${canonicalDomain}${window.location.pathname}${window.location.search}`;
      window.location.replace(canonical);
    }
    return;
  }

  if (!brandSlug) return;
  if (brandAutoRedirect === false) return;
  if (!SLUG_PATTERN.test(brandSlug)) return;
  const branded = `https://${brandSlug}.${canonicalDomain}${window.location.pathname}${window.location.search}`;
  window.location.replace(branded);
}
